import React, { useCallback } from "react";

import { defineMessages, useIntl } from "react-intl";

import { ActivityTypeRef } from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import { useModal } from "@mapmycustomers/ui";

import shouldWarnZohoKeepsPrimaryContactOnly from "@app/util/integrations/zohoSinglePersonActivity";

import styles from "./ZohoPrimaryContactConfirm.module.scss";

const messages = defineMessages({
  cancel: {
    id: "activity.zohoPrimaryContactConfirm.cancel",
    defaultMessage: "Go back",
    description: "Zoho primary contact confirmation cancel button",
  },
  content: {
    id: "activity.zohoPrimaryContactConfirm.content",
    defaultMessage:
      "Zoho Calls and Tasks hold one person. When this activity syncs, only the primary contact is added to Zoho. Everyone else stays in MapMyCustomers.",
    description: "Zoho primary contact confirmation content",
  },
  ok: {
    id: "activity.zohoPrimaryContactConfirm.ok",
    defaultMessage: "Save anyway",
    description: "Zoho primary contact confirmation ok button",
  },
  title: {
    id: "activity.zohoPrimaryContactConfirm.title",
    defaultMessage: "Only the primary contact will sync to Zoho",
    description: "Zoho primary contact confirmation title",
  },
});

type ConfirmIfNeededCallback = (
  activityType: ActivityTypeRef | undefined,
  peopleCount: number,
  onConfirm: () => void
) => void;

// Gates onConfirm behind a dialog when a Zoho org puts more than one person on an activity type
// Zoho can only hold one person on. Callers supply the people count, so this works both before a
// form save and before an immediate association write.
const useZohoPrimaryContactConfirm = (
  isZohoOrganization: boolean
): [ConfirmIfNeededCallback, React.ReactElement] => {
  const intl = useIntl();

  const [modal, contextHolder] = useModal();

  const confirmIfNeeded = useCallback<ConfirmIfNeededCallback>(
    (activityType, peopleCount, onConfirm) => {
      if (!shouldWarnZohoKeepsPrimaryContactOnly(isZohoOrganization, activityType, peopleCount)) {
        onConfirm();
        return;
      }
      modal.show({
        cancelButtonProps: {
          type: "text",
        },
        cancelText: intl.formatMessage(messages.cancel),
        className: styles.confirmationModal,
        content: intl.formatMessage(messages.content),
        okText: intl.formatMessage(messages.ok),
        onOk: onConfirm,
        title: intl.formatMessage(messages.title),
        type: "confirm",
      });
    },
    [intl, isZohoOrganization, modal]
  );

  return [confirmIfNeeded, contextHolder];
};

export default useZohoPrimaryContactConfirm;
